import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import {
  IonContent,
  IonPage,
  useIonRouter,
  useIonViewDidEnter,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import {
  AcceptAgreementFeature,
  AppFooter_v2 as AppFooter,
  BookingScheduleTime,
  BookingSpecialistInfo,
  Button_v2 as Button,
  CardModal,
  ClientSpecialistContractStatusEnum,
  Container,
  ContractStatusEnum,
  DaySlider,
  FrequentlyAskedQuestions,
  useDeepUpdateEffect,
  useEvent,
} from '@mindly/ui-components';
import { useDispatch, useSelector } from 'react-redux';

import BookingSkeleton from './BookingSkeleton';

import { setIsActive } from '../../redux/tabBar/reducer';
import { cleanCalendar, getPsychologistCalender } from '../../redux/calendarData/reducer';
import {
  psychologistSelectorById,
  specialistSelectorPositionById,
  specialistsSelector,
} from '../../redux/specialists/selector';
import { calendarDataSelector } from '../../redux/calendarData/selector';

import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';

import {
  ChooseAnotherButton,
  DaySliderWrapper,
  EmptyAnotherText,
  EmptyIconWrapper,
  EmptyMessage,
  EmptySlotsWrapper,
  FrequentlyAskedQuestionsWrapper,
  LocalTimeText,
  SpecialistListWrapper,
} from './BookingSystemPage.style';

import EmptySlotsIcon from '../../images/empty-slots.svg?react';
import { DateTime } from 'luxon';
import { userSelector } from '../../redux/user/selector';
import { LoadPaymentModal } from '../../modals/LoadPaymentModal';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import { FormattedMessage, useIntl } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import { BOOKING_FAQ, FIRST_BOOKING_FAQ, SUBSCRIPTION_PAUSE } from '../../intercom-articles';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useAppointment } from '../../hooks/useAppointment/useAppointment';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import useToast from '../../context/ToastContext/ToastContext';
import { fetchSpecialistsList } from '../../redux/specialists/thunk';
import { SignAgreementWidget } from '../../widgets';
import { useSignAgreementsContext } from '../../context/SignAgreementsContext/SignAgreements.context';
import { useTranslation } from 'react-i18next';
import { fetchLastContractBySpecialistId } from '../../api/contracts';
import { ISubscription } from '../../redux/subscriptions/types';
import { selectConsultationsBySpecialistAndClient } from '../../redux/consultations/selector';
import { useAnalytics } from 'src/shared';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { setIsModalOpened } from 'src/redux/mindlyReviews/reducer';
import { getCouponBalanceAvailability } from 'src/api/coupons';
import useIntercomPlugin from 'src/shared/hooks/useIntercomPlugin';

interface BookingSystemPageProps
  extends RouteComponentProps<{
    id: string;
    subscriptionID?: string;
    consultationID?: string;
  }> {}

export const BookingSystemPage: React.FC<BookingSystemPageProps> = ({ match }) => {
  const { id, consultationID, subscriptionID } = match.params;

  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const intl = useIntl();
  const {
    t,
    i18n: { language },
  } = useTranslation(['auth', 'app']);
  const { displayArticle } = useIntercomPlugin();
  const { bookingActionHandler, bookingActionLoadingStatus, clearStates, paymentStatus } = useAppointment();
  const {
    bookingPresentingElement,
    isSignAgreementOpen,
    currentOpenModal,
    closeModal,
    openModal,
    createContractBySpecialistId,
    fetchStatus: createContractStatus,
  } = useSignAgreementsContext();
  const { trackPredefinedEvent } = useAnalytics();

  const swiperRef = useRef<HTMLIonSlidesElement | null>(null);
  const loaderModalRef = useRef<HTMLIonModalElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false);
  const [activeDay, setActiveDay] = useState<number>(0);
  const [activeTime, setActiveTime] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<ISubscription | null | undefined>(null);
  const [contract, setContract] = useState<{ id: string; status: ClientSpecialistContractStatusEnum } | null>(null);
  const [isPageView, setIsPageView] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);
  const [isBalanceCoverSessionExactly, setIsBalanceCoverSessionExactly] = useState(false);

  const specialistList = useSelector(specialistsSelector);
  const specialist = useSelector(psychologistSelectorById(id));
  const specialistCalendar = useSelector(calendarDataSelector);
  const currentUser = useSelector(userSelector);
  const consultations = useSelector(selectConsultationsBySpecialistAndClient(id, currentUser?.id ?? ''));
  const activeSubscription = useSelector(lastActiveSubscriptionSelector(id));
  const [isAccept, setIsAccept] = useState(false);
  const navigation = useIonRouter();
  const isCanTrackChangeDay = useRef(false);
  const specialistPosition = useSelector(specialistSelectorPositionById(id));

  useIonViewWillEnter(async () => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    try {
      setIsLoading(true);
      dispatch(setIsActive(false));
      setActiveTime(null);

      !specialistList?.length && (await dispatch(fetchSpecialistsList(language)));

      await dispatch(getPsychologistCalender(id));
      const localSubscriptions = await dispatch(fetchSubscriptions(currentUser?.id)).unwrap();
      const localActiveSubscription = localSubscriptions.find(
        (el) => el.subscription_status === 'active' && el.therapist_id === id
      );
      const contractData = await fetchLastContractBySpecialistId(id ?? '');
      const response = await getCouponBalanceAvailability(id);
      setContract(contractData);
      setSubscription(localSubscriptions?.find((el) => el.therapist_id === id));
      setIsBalanceCoverSessionExactly(response?.is_coupon_cover_session_exact ?? false);

      if (localActiveSubscription?.pause_collection && activeSubscription) {
        showToast({
          text: intl.formatMessage({
            id: 'schedule.alert.pause',
            defaultMessage: 'Ваша підписка призупинена. Відновіть її, щоб записатися на сеанс.',
          }),
          intercomArticleID: SUBSCRIPTION_PAUSE,
        });

        return history.back();
      }
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [id, currentUser, language]);

  useIonViewDidEnter(() => {
    setIsEventTracked(false);
    setIsPageView(true);
  });

  useIonViewWillLeave(() => {
    setIsPageView(false);
  });

  useIonViewWillLeave(() => {
    if (
      !navigation.routeInfo.pathname.startsWith(PrivateRoutes.PSYCHOLOGIST_PROFILE_TAB.replace('/:id', '')) &&
      !navigation.routeInfo.pathname.startsWith(PrivateRoutes.SESSION_DETAIL.replace('/:id', ''))
    ) {
      dispatch(setIsActive(true));
    }
    setActiveDay(0);
    setActiveTime(null);
    dispatch(cleanCalendar());
    setIsAccept(false);
    clearStates();
    loaderModalRef.current = null;
  }, [navigation.routeInfo]);

  const handleSelectDay = useCallback(
    (a: number) => {
      setActiveTime(null);
      setActiveDay(a);
      swiperRef.current?.slideTo(a);
    },
    [specialistCalendar, id]
  );

  const handleSwipeSelectDay = useCallback(
    async (e) => {
      const index = await e.target.getActiveIndex();
      setActiveTime(null);
      setActiveDay(index);
    },
    [specialistCalendar, id]
  );

  const handleSelectTime = useCallback(
    async (time: number) => {
      setActiveTime(time);
    },
    [specialistCalendar, activeTime, activeDay, id, currentUser?.id, bookingActionHandler]
  );

  const rescheduleConsultation = async (a: number) => {
    const reschedulingDate = DateTime.fromFormat(
      `${specialistCalendar[activeDay].fromDataDay} ${specialistCalendar[activeDay].timeArray[a]}`,
      'yyyy-LL-dd H:mm'
    )
      .toUTC()
      .toSeconds();

    history.push(
      PrivateRoutes.RESCHEDULE_CONFIRM.replace(':sessionID', consultationID ?? '').replace(
        ':time',
        `${reschedulingDate}`
      )
    );
  };

  useEffect(() => {
    if (bookingActionLoadingStatus === 'success' && !isBalanceCoverSessionExactly) {
      setTimeout(() => {
        history.push(PrivateRoutes.CONSULTATION);
        loaderModalRef.current?.dismiss();
        dispatch(setIsModalOpened(true));
      }, 2000);
    }
  }, [bookingActionLoadingStatus]);

  const handleSubmit = useEvent(async () => {
    setIsSubmitButtonLoading(true);
    if (activeTime === null) return;

    if (consultationID) {
      await rescheduleConsultation(activeTime);
      return;
    }

    if (specialist && !subscriptionID) {
      trackPredefinedEvent('TRIAL_SCHEDULE_DATE_SELECTED', {
        specialist_id: specialist.id,
        specialist_name: `${specialist.first_name} ${specialist.last_name}`,
        date_chosen: DateTime.fromFormat(
          `${specialistCalendar[activeDay].fromDataDay} ${specialistCalendar[activeDay].timeArray[activeTime]}`,
          'yyyy-LL-dd H:mm'
        )
          .toUTC()
          .toISO(),
        specialist_order_position: specialistPosition,
      });
    }

    if (isTrialSession && specialist?.id) {
      if (currentUser?.country && currentUser?.dob) {
        await createContractBySpecialistId(specialist.id);
      } else {
        history.push(PrivateRoutes.NEED_UPDATE_PERSONAL_DATA);
        return;
      }
    }

    const formattedDate = DateTime.fromFormat(
      `${specialistCalendar[activeDay].fromDataDay} ${specialistCalendar[activeDay].timeArray[activeTime]}`,
      'yyyy-LL-dd H:mm'
    )
      .toUTC()
      .toISO();

    await bookingActionHandler(id, formattedDate);
    setIsSubmitButtonLoading(false);
  });

  const scheduleItems = useMemo(() => {
    if (!specialistCalendar?.[activeDay]) return [];

    return specialistCalendar[activeDay]?.timeArray?.map((i, idx) => ({
      title: DateTime.fromFormat(`${i}`, 'H:mm').toLocaleString(DateTime.TIME_SIMPLE),
      active: idx === activeTime,
    }));
  }, [activeDay, activeTime, specialistCalendar]);

  const availableTimeSlots = useMemo(() => {
    if (!specialistCalendar[activeDay]) return undefined;
    const date = specialistCalendar[activeDay].fromDataDay;
    return {
      date,
      available_times: scheduleItems.map((i) => i.title),
    };
  }, [specialistCalendar, activeDay, scheduleItems]);

  useDeepUpdateEffect(() => {
    if (!specialist) return;
    if (!isCanTrackChangeDay.current) return;
    if (!availableTimeSlots) return;
    trackPredefinedEvent('TRIAL_SCHEDULE_SCREEN_SLOTS_VIEWED', {
      specialist_id: specialist.id,
      specialist_name: `${specialist.first_name} ${specialist.last_name}`,
      available_times_slots: availableTimeSlots,
    });
  }, [specialist, activeDay, availableTimeSlots]);

  useEffect(() => {
    if (!isPageView) return;
    if (isLoading) return;
    if (!specialist) return;
    if (isEventTracked) return;
    if (subscriptionID && !subscription) return;
    if (!currentUser) return;

    setIsEventTracked(true);
    isCanTrackChangeDay.current = true;
    trackPredefinedEvent(subscriptionID ? 'SUBSCRIPTION_SCHEDULE_SCREEN_VIEWED' : 'TRIAL_SCHEDULE_SCREEN_VIEWED', {
      specialist_id: specialist.id,
      specialist_name: `${specialist.first_name} ${specialist.last_name}`,
      ...(subscriptionID && {
        subscription_id: subscriptionID,
        subscription_length: subscription?.subscription_interval_in_month,
        subscription_balance: (subscription?.session_count ?? 0) - (subscription?.used_balance ?? 0),
      }),
      specialist_order_position: specialistPosition,
      available_times_slots: availableTimeSlots,
    });
  }, [
    specialist,
    isPageView,
    isEventTracked,
    subscriptionID,
    subscription,
    isLoading,
    currentUser,
    activeDay,
    availableTimeSlots,
    specialistPosition,
  ]);

  // This var checks only if the contaract exist
  const isTrialSession = useMemo(() => {
    return (
      specialist?.mindly_contract_status === ContractStatusEnum.active &&
      !contract?.id &&
      !subscription &&
      !consultations?.some((c) => c.psychologist_id === id)
    );
  }, [specialist?.mindly_contract_status, subscription, contract?.id, consultations, id]);

  return (
    <IonPage ref={bookingPresentingElement}>
      <PageHeader
        backChevron
        intercomId={subscriptionID ? BOOKING_FAQ : FIRST_BOOKING_FAQ}
        title={intl.formatMessage({
          id: 'bocking.title',
          defaultMessage: 'Вибери час',
        })}
        loading={isLoading}
        showPromoCodeTimer
        subtitle={
          activeSubscription?.subscription_status === 'active' || isBalanceCoverSessionExactly
            ? intl.formatMessage(
                {
                  id: 'bocking.step',
                  defaultMessage: 'Крок {from} із {to}',
                },
                { from: 1, to: 1 }
              )
            : intl.formatMessage(
                {
                  id: 'bocking.step',
                  defaultMessage: 'Крок {from} із {to}',
                },
                { from: 1, to: 2 }
              )
        }
      />
      <LoadPaymentModal
        ref={loaderModalRef}
        fetchingStatus={bookingActionLoadingStatus}
        paymentStatus={paymentStatus}
        loadType={isBalanceCoverSessionExactly && !subscriptionID ? 'payment' : 'subscriptionBooking'}
        isTrial={isBalanceCoverSessionExactly && !subscriptionID}
        history={history}
      />
      <IonContent>
        <Container>
          {isLoading ? (
            <BookingSkeleton />
          ) : (
            <>
              <SpecialistListWrapper>
                <BookingSpecialistInfo
                  title={intl.formatMessage(
                    {
                      id: 'bocking.session.hour',
                      defaultMessage: 'Сеанс {minutes} хвилин',
                    },
                    { minutes: specialist?.session_duration_minutes ?? 50 }
                  )}
                  subtitle={
                    consultationID
                      ? intl.formatMessage({
                          id: 'bocking.chose.title',
                          defaultMessage: 'Вибери час і день, на який тобі потрібно перенести сеанс',
                        })
                      : intl.formatMessage({
                          id: 'bocking.chose.subtitle',
                          defaultMessage: 'Виберіть день та час, на який Ви хочете забронювати сеанс',
                        })
                  }
                  avatar={specialist?.avatar_link ?? ''}
                  name={specialist?.first_name ?? ''}
                />
              </SpecialistListWrapper>
              {specialistCalendar.length === 0 ? (
                <EmptySlotsWrapper>
                  <EmptyIconWrapper>
                    <EmptySlotsIcon />
                  </EmptyIconWrapper>
                  <EmptyMessage>
                    <FormattedMessage id="bocking.empty" defaultMessage="Психолог не має вільного часу." />
                  </EmptyMessage>
                  <EmptyAnotherText>
                    <FormattedMessage id="bocking.another" defaultMessage="Виберіть іншого або зачекайте." />
                  </EmptyAnotherText>
                  <ChooseAnotherButton
                    buttonType={'primary'}
                    onClick={() => history.push(PrivateRoutes.PSYCHOLOGIST_LIST)}
                  >
                    <FormattedMessage id={'bocking.another.choose'} defaultMessage={'Вибрати іншого'} />
                  </ChooseAnotherButton>
                  <FrequentlyAskedQuestionsWrapper
                    onClick={() => displayArticle(subscriptionID ? BOOKING_FAQ : FIRST_BOOKING_FAQ)}
                  >
                    <FrequentlyAskedQuestions
                      text={intl.formatMessage({
                        id: 'bocking.faq',
                        defaultMessage: 'Питання що часто задаються',
                      })}
                      alignment={'left'}
                    />
                  </FrequentlyAskedQuestionsWrapper>
                </EmptySlotsWrapper>
              ) : (
                <>
                  <DaySliderWrapper>
                    <DaySlider
                      ref={swiperRef}
                      onIonSlideDidChange={handleSwipeSelectDay}
                      slides={specialistCalendar.map((d) =>
                        DateTime.fromFormat(d.fromDataDay, 'yyyy-LL-dd').toSeconds()
                      )}
                      onDayClick={(index) => handleSelectDay(index)}
                      locale={language}
                      translations={{
                        tomorrow: intl.formatMessage({
                          id: 'general.tomorrow',
                          defaultMessage: 'Завтра',
                        }),
                        today: intl.formatMessage({
                          id: 'general.today',
                          defaultMessage: 'Сьогодні',
                        }),
                      }}
                    />
                  </DaySliderWrapper>
                  <LocalTimeText>
                    <FormattedMessage id={'bocking.for.localTime'} defaultMessage={'За Вашим місцевим часом'} />
                  </LocalTimeText>
                  {scheduleItems.length > 0 && (
                    <BookingScheduleTime
                      items={scheduleItems}
                      onClick={(index) => {
                        handleSelectTime(index);
                        isomorficHapticsImpact({ style: ImpactStyle.Light });
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Container>
      </IonContent>

      {!isLoading && (
        <AppFooter>
          {isTrialSession && (
            <AcceptAgreementFeature
              handlePressLink={() => openModal('booking')}
              translations={[
                t('app:confirm_agreement.text.0'),
                t('app:confirm_agreement.text.1'),
                t('app:confirm_agreement.text.2'),
              ]}
              isAccepted={isAccept}
              setIsAccepted={setIsAccept}
            />
          )}
          <Button
            size="large"
            fullWidth
            isLoading={createContractStatus === 'pending' || isSubmitButtonLoading}
            onClick={handleSubmit}
            disabled={(!isAccept && isTrialSession) || activeTime === null}
          >
            {t('auth.button.next')}
          </Button>
        </AppFooter>
      )}

      <CardModal
        presentingElement={bookingPresentingElement.current ? bookingPresentingElement.current : undefined}
        isOpen={isSignAgreementOpen && currentOpenModal === 'booking'}
        onDidDismiss={closeModal}
      >
        {specialist?.id && (
          <SignAgreementWidget
            type="new"
            specialistId={specialist.id}
            contractStatus="not_signed"
            onApply={() => {
              setIsAccept(true);
              closeModal();
            }}
          />
        )}
      </CardModal>
    </IonPage>
  );
};
